<template>
  <VFiltersPopup
      popup-class="order-outer-filter"
      :title-list="titleList"
      @submit="applyFilters"
      @clear="clearFilters"
  >
    <section class="outer-filter-container">
      <VButtonGroup>
        <VButton
            :theme="draft.isMine ? 'primary' : 'default'"
            @click="draft.isMine = true"
        >
          <template #icon>
            <VIcon material="person_black" />
          </template>
          <slot name="label-mine">
            Мои заказы
          </slot>
        </VButton>
        <VButton
            :theme="draft.isMine ? 'default' : 'primary'"
            @click="draft.isMine = false"
        >
          <template #icon>
            <VIcon material="filter_alt" />
          </template>
          <slot name="label-all">
            Все заказы
          </slot>
        </VButton>
      </VButtonGroup>

      <div class="wrap">
        <VSelect
            v-model="draft.responsible"
            class="responsible-input"
            placeholder="Ответственный"
            :disabled="draft.isMine"
            :search-function="$event => PersonsService.searchPersons($event, currentPartnerId)"
        />

        <VSelectV2
            v-if="$core.currentPartner.isBuyer"
            v-model="draft.seller"
            class="seller-input"
            :disabled="filters.isLocked('seller')"
            placeholder="Поставщик"
            :data="sellersLoader"
        />

        <VSelect
            v-model="draft.deliveryType"
            :disabled="filters.isLocked('deliveryType')"
            placeholder="Выберите тип доставки"
            :options="deliveryTypeOptions"
        />

        <VSelect
            v-if="$core.currentPartner.isBuyer"
            v-model="draft.orderCategory"
            :disabled="filters.isLocked('orderCategory')"
            clearable
            placeholder="Выберите категорию заказа"
            :options="orderCategoryOptions"
        />
      </div>

      <section class="wrap">
        <VCheckboxGroup
            v-model="draft.isOfferBased"
            :disabled="filters.isLocked('isOfferBased')"
            label="Тип заказа"
            layout="vertical"
            type-radio
            checkbox-layout="horizontal"
            :options="$core.dictionaries.orderIsOfferBased.toArray()"
            option-label="name"
        />
      </section>

      <section class="wrap">
        <VCheckboxGroup
            v-model="draft.isSamples"
            :disabled="filters.isLocked('isSamples')"
            label="Наименование раздела"
            layout="vertical"
            type-radio
            checkbox-layout="horizontal"
            :options="$core.dictionaries.orderIsSamples.toArray()"
            option-label="name"
        />
      </section>

      <section
          v-if="$core.currentPartner.isBuyer"
          class="wrap"
      >
        <VCheckboxGroup
            v-model="draft.isForTestsOnly"
            :disabled="filters.isLocked('isForTestsOnly')"
            label="Идут образцы в производство"
            layout="vertical"
            type-radio
            checkbox-layout="horizontal"
            :options="$core.dictionaries.orderIsForTestsOnly.toArray()"
            option-label="name"
        />
      </section>

      <section
          v-if="$core.currentPartner.isBuyer && this.showPaymentAndDeliverySection"
          class="mds-btn-group"
      >
        <VCheckboxGroup
            v-if="showDeliveryOptions"
            v-model="draft.deliveryStatus"
            :disabled="filters.isLocked('deliveryStatus')"
            label="Доставка"
            :checkbox-layout="showPaymentOptions ? 'vertical' : 'horizontal'"
            :options="$core.dictionaries.orderDeliveryStatus.toArray()"
            option-label="name"
            type-radio
        />
        <VCheckboxGroup
            v-if="showPaymentOptions"
            v-model="draft.paymentStatus"
            :disabled="filters.isLocked('paymentStatus')"
            label="Оплата"
            :checkbox-layout="showDeliveryOptions ? 'vertical' : 'horizontal'"
            :options="$core.dictionaries.orderPaymentStatus.toArray()"
            option-label="name"
            type-radio
        />
      </section>
    </section>
  </VFiltersPopup>
</template>

<style lang="scss" src="./OrdersPageFilterComponent.scss"/>

<script lang="ts">
import VFiltersPopup from '@/components/VFiltersPopup/VFiltersPopup.vue';
import PersonsService from '@/domains/Persons/PersonsService';
import OrdersPageFilter from "@/domains/Orders/components/OrdersPage/OrdersPageFilter/OrdersPageFilter";

export default defineComponent({
  name: 'OrdersPageFilterComponent',

  components: {
    VFiltersPopup,
  },

  props: {
    filters: {
      type: Object as PropType<OrdersPageFilter>,
      required: true,
    },
    showPaymentOptions: {
      type: Boolean,
      default: false,
    },
    showDeliveryOptions: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['orders-page-filter:updated'],

  data() {
    return {
      PersonsService,
      sellersLoader: this.$core.loader.sellers(),

      // Draft - черновик для редактирования
      draft: this.$core.utils.cloneDeep(this.filters),
    };
  },

  computed: {
    titleList(): string[] {
      const texts: string[] = [];

      // Используем filters (не draft) - показываем применённые фильтры
      if (this.filters.isMine) {
        texts.push('Мои заказы');
      } else {
        if (this.filters.responsible) {
          texts.push(this.filters.responsible.name);
        } else {
          texts.push('Все заказы');
        }
      }

      if (this.filters.seller?.name) {
        texts.push(this.filters.seller.name);
      }

      if (this.filters.orderCategory) {
        texts.push(this.filters.orderCategory.name);
      }

      if (typeof this.filters.isOfferBased?.value === 'boolean') {
        texts.push(this.filters.isOfferBased.name);
      }

      if (typeof this.filters.isSamples?.value === 'boolean') {
        texts.push(this.filters.isSamples.name);
      }

      if (typeof this.filters.isForTestsOnly?.value === 'boolean') {
        texts.push(this.filters.isForTestsOnly.name);
      }

      if (this.filters.deliveryType?.id) {
        texts.push(this.filters.deliveryType.name);
      }

      if (this.showDeliveryOptions && this.filters.deliveryStatus.id !== 'all') {
        texts.push(this.filters.deliveryStatus.name);
      }

      if (this.showPaymentOptions && this.filters.paymentStatus.id !== 'all') {
        texts.push(this.filters.paymentStatus.name);
      }

      return texts;
    },

		orderCategoryOptions() {
			return this.$core.dictionaries.orderCategories.toArray();
		},

		deliveryTypeOptions() {
			return this.$core.dictionaries.deliveryTypes.toArray();
		},

    currentPartnerId() { return this.$core.currentPartner!.id },

    showPaymentAndDeliverySection() {
      return this.showPaymentOptions || this.showDeliveryOptions;
    }
  },

  watch: {
    /** Фильтр поменяли мимо черновика (filter.update из фильтра колонки реестра) — черновик повторяет его */
    filters: {
      deep: true,
      handler(value) {
        this.draft = this.$core.utils.cloneDeep(value);
      },
    },
  },

  methods: {
    applyFilters() {
      this.draft.apply();

      this.$emit(OrdersPageFilter.Events.Updated, this.draft);

      // Создаём новый draft для следующего редактирования
      this.draft = this.$core.utils.cloneDeep(this.draft);
    },

    clearFilters() {
      // Сбрасываем filters
      this.filters.reset();

      // Создаём новый draft из сброшенного filters
      this.draft = this.$core.utils.cloneDeep(this.filters);

      // Эмитим родителю
      this.$emit(OrdersPageFilter.Events.Updated, this.filters);
    },
  },
});
</script>
